import React, { useState, useEffect } from 'react';
import {createLoan, getBranches} from '../../Api/Api';
import { useWorkshop } from "../Common/WorkshopContext";
import { useNotification } from "../Notification/NotificationContext";

const LoanModal = ({ show, customerId, onClose }) => {
    const [amount, setAmount] = useState('');
    const [downPayment, setDownPayment] = useState('');
    const [tenure, setTenure] = useState('');
    const [interestRate, setInterestRate] = useState('');
    const [startDate, setStartDate] = useState("");
    const [loanType, setLoanType] = useState("Flat");
    const [branchId, setBranchId] = useState("");  // New state
    const [branches, setBranches] = useState([]);

    const { workshopId } = useWorkshop();
    const { showNotification } = useNotification();

    // Fetch branches on modal open
    useEffect(() => {
        if (show) {
            getBranches(0,100)
                .then(res => setBranches(res.data.content))
                .catch(err => showNotification("Failed to load branches", "error"));
        }
    }, [show, workshopId]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!branchId) {
            showNotification("Please select a branch", "error");
            return;
        }

        const loanData = {
            customerId,
            amount,
            tenure,
            interestRate,
            downPayment,
            startDate,
            workshopId,
            loanType,
            branchId // Add branch
        };

        try {
            await createLoan(loanData);
            showNotification("Created Loan Successfully", "success");
            onClose();
        } catch (error) {
            showNotification(error.message, "error");
        }
    };

    if (!show) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white w-full max-w-lg mx-auto rounded-2xl shadow-lg p-6 md:p-8">

                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-800">
                        Request Loan
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-700 text-2xl font-bold"
                    >
                        &times;
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">

                    {/* Branch */}
                    <div className="md:col-span-2">
                        <label className="text-sm text-gray-600 mb-1 block">Branch</label>
                        <select
                            value={branchId}
                            onChange={(e) => setBranchId(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                        >
                            <option value="">-- Select Branch --</option>
                            {branches.map(b => (
                                <option key={b.id} value={b.id}>{b.name} ({b.city})</option>
                            ))}
                        </select>
                    </div>

                    {/* Amount */}
                    <div>
                        <label className="text-sm text-gray-600 mb-1 block">Amount</label>
                        <input
                            type="number"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            placeholder="Loan Amount"
                            className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                        />
                    </div>

                    {/* Down Payment */}
                    <div>
                        <label className="text-sm text-gray-600 mb-1 block">Down Payment</label>
                        <input
                            type="number"
                            value={downPayment}
                            onChange={(e) => setDownPayment(e.target.value)}
                            placeholder="Down Payment"
                            className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                        />
                    </div>

                    {/* Tenure */}
                    <div>
                        <label className="text-sm text-gray-600 mb-1 block">Tenure (months)</label>
                        <input
                            type="number"
                            value={tenure}
                            onChange={(e) => setTenure(e.target.value)}
                            placeholder="Tenure"
                            className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                        />
                    </div>

                    {/* Interest Rate */}
                    <div>
                        <label className="text-sm text-gray-600 mb-1 block">Interest Rate (%)</label>
                        <input
                            type="number"
                            value={interestRate}
                            onChange={(e) => setInterestRate(e.target.value)}
                            placeholder="Interest Rate"
                            className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                        />
                    </div>

                    {/* Start Date */}
                    <div>
                        <label className="text-sm text-gray-600 mb-1 block">Start Date</label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                        />
                    </div>

                    {/* Loan Type */}
                    <div>
                        <label className="text-sm text-gray-600 mb-1 block">Loan Type</label>
                        <select
                            value={loanType}
                            onChange={(e) => setLoanType(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                        >
                            <option value="Flat">Flat</option>
                            <option value="Reducing">Reducing</option>
                        </select>
                    </div>
                </form>

                {/* Buttons */}
                <div className="mt-6 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-5 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        className="px-6 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
                    >
                        Submit
                    </button>
                </div>
            </div>
        </div>
    );
};

export default LoanModal;